import { ReactElement, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import DailyGhinCsvUpload from '../components/ghin/DailyGhinCsvUpload';
import { useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useGhinMutators } from '../dataMutators/useGhinMutators';
import GhinDaily from '../types/GhinDaily';
import Person from '../types/Person';
import { HANDICAP_LABELS } from '../utils/handicapUtils';

export default function GhinUpdatePage(): ReactElement {
  const { isGod } = useAuth();
  const { setPageName } = useApp();
  const { addDaily, copyAnnualMapping } = useGhinMutators();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCopyMapping, setShowCopyMapping] = useState(false);
  const [copyYear, setCopyYear] = useState<string>(new Date().getFullYear().toString());

  useEffect(() => {
    setPageName(`${HANDICAP_LABELS.INDEX} Update`);
  }, [setPageName]);

  const validateUser = (userProfile: Person | null | undefined) => {
    return userProfile && isGod ? true : false;
  };

  const handleSubmit = async (ghinDailies: GhinDaily[]) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      // Update daily docs one at a time, in order, rather than in parallel
      for (const ghinDaily of ghinDailies) {
        await addDaily(ghinDaily);
      }
      setShowSuccess(true);
      // Wait a moment to show the success message before navigating
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error('Error submitting GHIN data:', error);
      setError(`Failed to upload ${HANDICAP_LABELS.INDEX} data. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCopyMappingClick = () => {
    setShowCopyMapping(true);
  };

  const handleCopyMappingCancel = () => {
    setShowCopyMapping(false);
  };

  const handleCopyMappingSubmit = async () => {
    const year = parseInt(copyYear, 10);
    if (isNaN(year)) {
      setError('Please enter a valid year.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const success = await copyAnnualMapping(year);
      if (success) {
        setShowSuccess(true);
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        setError('Failed to copy mapping. Please try again.');
      }
    } catch (error) {
      console.error('Error copying mapping:', error);
      setError('Failed to copy mapping. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GratiPageWrapper validateUser={validateUser}>
      <Stack spacing={2}>
        {!showCopyMapping ? (
          <>
            <DailyGhinCsvUpload onSubmit={handleSubmit} onCancel={handleCancel} />
            <Button variant="outlined" onClick={handleCopyMappingClick}>
              Copy Mapping
            </Button>
          </>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="Year"
              value={copyYear}
              onChange={(e) => setCopyYear(e.target.value)}
              type="number"
              disabled={isSubmitting}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleCopyMappingSubmit}
                disabled={isSubmitting}
              >
                Copy
              </Button>
              <Button
                variant="outlined"
                onClick={handleCopyMappingCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Snackbar
          open={showSuccess}
          autoHideDuration={1500}
          onClose={() => setShowSuccess(false)}
          message={showCopyMapping ? "Mapping copy requested" : `${HANDICAP_LABELS.INDEX} data uploaded successfully`}
        />
      </Stack>
    </GratiPageWrapper>
  );
}
